/**
 * DSP inspector / engineering mode (spec 27, 28, 57).
 *
 * Selecting a node in the chain opens its real compiled state here: the actual
 * FIR that is running, the actual biquad cascade, the actual harmonic
 * amplitudes, the actual latency and DSP cost. These are read from the compiled
 * payload that was sent to the worklet, not recomputed for display -- so what is
 * plotted is what is running.
 */

import { useMemo } from 'react';
import { registry } from '../../database';
import type { AmpProfile, AnyProfile, DacProfile, HeadphoneProfile, SpeakerProfile } from '../../types/hardware';
import { CABLE_TYPE_RATIONALE, cableAffectsSignalPath, isHeadphone, isSpeaker } from '../../types/hardware';
import { CABLE_SLOTS, CABLE_SLOT_LABEL, CABLE_SLOT_TYPE } from '../../types/system';
import { NL_NAMES } from '../../types/compiled';
import type { CompiledBiquad } from '../../types/compiled';
import { cascadeMagDb } from '../../dsp/biquad';
import { loadInteractionCurve } from '../../dsp/compile';
import { evalFR, flat } from '../../types/curves';
import type { FRCurve } from '../../types/curves';
import { engine } from '../../audio/engine';
import { useApp } from '../../stores/appStore';
import {
  ConfidenceBadge,
  ConfidenceBar,
  Note,
  PRICE_STATUS_LABEL,
  SectionTitle,
  Segmented,
  Slider,
  Stat,
  Toggle,
  fmtHz,
  fmtPrice,
} from './primitives';
import { FrequencyPlot, HarmonicBars, ImpedancePlot, ImpulsePlot, TransferPlot } from './plots';

export function Inspector() {
  const node = useApp((s) => s.inspectorNode);
  const activeSystem = useApp((s) => s.activeSystem);
  const compiled = useApp((s) => s.compiled[activeSystem]);
  const chain = useApp((s) => (activeSystem === 'A' ? s.project.systemA : s.project.systemB));
  const setInspector = useApp((s) => s.setInspector);

  if (node?.startsWith('library-detail:')) {
    const p = registry.get(node.slice('library-detail:'.length));
    return (
      <div className="panel-body">
        <div className="panel-head">
          <span>Profile detail</span>
          <div className="spacer" />
          <button className="btn sm" onClick={() => setInspector(null)}>
            Close
          </button>
        </div>
        {p ? <ProfileDetail profile={p} /> : <div className="panel-section dim">Profile not found.</div>}
      </div>
    );
  }

  return (
    <>
      <div className="panel-head">
        <span>Inspector — System {activeSystem}</span>
        <div className="spacer" />
        {node && (
          <button className="btn sm" onClick={() => setInspector(null)}>
            Clear
          </button>
        )}
      </div>
      <div className="panel-body">
        {!node && <ChainSummary />}
        {node === 'source' && <SourceInspector />}
        {node === 'dac' && <DacInspector />}
        {node === 'amp' && <AmpInspector />}
        {node === 'output' && <OutputInspector />}
        {node === 'spatial' && <SpatialInspector />}
        {node === 'master' && <MasterInspector />}
        {compiled && node && (
          <div className="panel-section">
            <SectionTitle>Compiled cost</SectionTitle>
            <div className="stat-grid">
              <Stat label="DAC" value={`${compiled.cost.dac}`} sub="MAC/sample" />
              <Stat label="Amp" value={`${compiled.cost.amp}`} sub="MAC/sample" />
              <Stat label="Output" value={`${compiled.cost.output}`} sub="MAC/sample" />
              <Stat label="Spatial" value={`${compiled.cost.spatial}`} sub="MAC/sample" />
              <Stat label="Latency" value={compiled.latencySamples.toFixed(0)} sub={`${((compiled.latencySamples / (engine.sampleRate || 48000)) * 1000).toFixed(2)} ms`} />
              <Stat label="Chain gain" value={`${(20 * Math.log10(Math.max(1e-9, compiled.chainGain1k))).toFixed(2)}`} sub="dB @ 1 kHz" />
            </div>
          </div>
        )}
        {chain.bypass && node && null}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------

function useActive() {
  const activeSystem = useApp((s) => s.activeSystem);
  const chain = useApp((s) => (activeSystem === 'A' ? s.project.systemA : s.project.systemB));
  const compiled = useApp((s) => s.compiled[activeSystem]);
  const headroom = useApp((s) => s.headroom[activeSystem]);
  return { activeSystem, chain, compiled, headroom };
}

function unflatten(flatArr: Float32Array): CompiledBiquad[] {
  const out: CompiledBiquad[] = [];
  for (let i = 0; i + 4 < flatArr.length; i += 5) {
    out.push({ b0: flatArr[i], b1: flatArr[i + 1], b2: flatArr[i + 2], a1: flatArr[i + 3], a2: flatArr[i + 4] });
  }
  return out;
}

function cascadeToCurve(sections: CompiledBiquad[], fs: number): FRCurve {
  if (!sections.length) return flat();
  const points: Array<readonly [number, number]> = [];
  for (let i = 0; i < 80; i++) {
    const f = 10 * Math.pow(3000, i / 79);
    if (f > fs / 2) break;
    points.push([f, cascadeMagDb(sections, fs, f)]);
  }
  return { kind: 'fr', points: points as FRCurve['points'], interp: 'log-pchip' };
}

function firToCurve(taps: Float32Array | null, fs: number): FRCurve {
  if (!taps || !taps.length) return flat();
  const points: Array<readonly [number, number]> = [];
  for (let i = 0; i < 90; i++) {
    const f = 10 * Math.pow(3000, i / 89);
    if (f > fs / 2) break;
    const w = (2 * Math.PI * f) / fs;
    let re = 0;
    let im = 0;
    for (let k = 0; k < taps.length; k++) {
      re += taps[k] * Math.cos(w * k);
      im -= taps[k] * Math.sin(w * k);
    }
    points.push([f, 20 * Math.log10(Math.max(1e-9, Math.hypot(re, im)))]);
  }
  return { kind: 'fr', points: points as FRCurve['points'], interp: 'log-pchip' };
}

// ---------------------------------------------------------------------------

function ChainSummary() {
  const { chain, compiled, headroom } = useActive();
  const dac = registry.get(chain.dacId);
  const amp = registry.get(chain.ampId);
  const out = registry.get(chain.outputId);
  return (
    <div className="panel-section">
      <SectionTitle>Chain</SectionTitle>
      <p className="dim small" style={{ marginTop: 0 }}>
        Select any node in the chain to inspect what it is actually doing to the signal.
      </p>
      <div className="stat-grid">
        <Stat label="DAC" value={dac?.model ?? '--'} sub={dac?.manufacturer} />
        <Stat label="Amplifier" value={amp?.model ?? '--'} sub={amp?.manufacturer} />
        <Stat label="Output" value={out?.model ?? '--'} sub={out?.manufacturer} />
      </div>
      {headroom && (
        <div style={{ marginTop: 8 }}>
          <Note tone={!headroom.valid ? 'danger' : headroom.headroomDb < 3 ? 'warn' : undefined}>
            <b>Drive headroom {headroom.valid ? `${headroom.headroomDb.toFixed(1)} dB` : 'invalid pairing'}.</b> {headroom.note}
          </Note>
        </div>
      )}
      {compiled?.warnings.map((w, i) => (
        <div key={i} style={{ marginTop: 8 }}>
          <Note tone="warn">{w}</Note>
        </div>
      ))}

      <CableSummary />
    </div>
  );
}

/**
 * What every cable in the chain is doing, side by side.
 *
 * Deliberately shows the unmodelled slots too, with an explicit zero, rather
 * than hiding them. A reader should be able to see at a glance which parts of
 * their cabling the engine believes can matter and which it does not -- that
 * comparison is the honest content of this feature.
 */
function CableSummary() {
  const { chain, compiled } = useActive();
  const activeSystem = useApp((s) => s.activeSystem);
  const cableFor = useApp((s) => s.cableFor);
  if (!chain.cables) return null;

  const rows = CABLE_SLOTS.map((slot) => {
    const c = cableFor(activeSystem, slot);
    const r = compiled?.cables?.[slot];
    const type = CABLE_SLOT_TYPE[slot];
    /*
     * The compiled report is the authority on whether this cable is doing
     * anything, not the cable's type. A loudspeaker cable is a modelled type,
     * but with a headphone selected as the output it is not connected to
     * anything, and reportCable() says so by returning modelled:false. Deciding
     * that here from the type alone would print "<0.01 dB" for a cable that is
     * not in the circuit at all.
     */
    const modelled = r ? r.modelled : cableAffectsSignalPath(type);
    return { slot, c, r, type, modelled };
  });
  const anySelected = rows.some((r) => r.c);

  return (
    <div style={{ marginTop: 12 }}>
      <SectionTitle>Cabling</SectionTitle>
      <table className="data">
        <thead>
          <tr>
            <th>Slot</th>
            <th>Cable</th>
            <th className="num">Length</th>
            <th className="num">Effect</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ slot, c, r, modelled }) => (
            <tr key={slot} title={r?.summary}>
              <td className="dim">{CABLE_SLOT_LABEL[slot]}</td>
              <td>{c ? `${c.manufacturer} ${c.model}` : <span className="faint">none</span>}</td>
              <td className="num dim">{c ? `${chain.cables[slot].lengthM} m` : ''}</td>
              <td className="num">
                {!c ? (
                  ''
                ) : modelled ? (
                  <span className={r && r.maxDeviationDb >= 0.05 ? 'warnc' : 'okc'}>
                    {r ? (r.maxDeviationDb < 0.005 ? '<0.01 dB' : `${r.maxDeviationDb.toFixed(2)} dB`) : '--'}
                  </span>
                ) : (
                  <span className="dim">0.00 dB</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {anySelected && (
        <div style={{ marginTop: 8 }}>
          <Note>
            Power, USB and Ethernet are listed at exactly 0.00 dB because the engine applies no processing for them.
            {' '}
            {CABLE_TYPE_RATIONALE.power}
          </Note>
        </div>
      )}
    </div>
  );
}

function SourceInspector() {
  const metadata = useApp((s) => s.metadata);
  const analysis = useApp((s) => s.analysis);
  const container = useApp((s) => s.container);
  if (!metadata) return <div className="panel-section dim">No file loaded.</div>;
  return (
    <>
      <div className="panel-section">
        <SectionTitle>File</SectionTitle>
        <div className="stat-grid">
          <Stat label="Container" value={metadata.container} />
          <Stat label="Codec" value={metadata.codec} />
          <Stat label="Sample rate" value={`${(metadata.sampleRate / 1000).toFixed(1)} kHz`} />
          <Stat label="Bit depth" value={metadata.bitDepth ? `${metadata.bitDepth} bit` : 'n/a'} sub={container?.fromHeader ? 'from header' : 'from decoder'} />
          <Stat label="Channels" value={metadata.channels} />
          <Stat label="Lossless" value={metadata.lossless ? 'yes' : 'no'} tone={metadata.lossless ? 'ok' : 'warn'} />
        </div>
      </div>
      {analysis && (
        <div className="panel-section">
          <SectionTitle>Measured content</SectionTitle>
          <div className="stat-grid">
            <Stat label="Peak" value={`${analysis.peakDbFS.toFixed(2)}`} sub="dBFS" />
            <Stat label="True peak" value={analysis.truePeakDbTP !== null ? analysis.truePeakDbTP.toFixed(2) : '--'} sub="dBTP" tone={(analysis.truePeakDbTP ?? -1) > 0 ? 'warn' : undefined} />
            <Stat label="Integrated" value={analysis.lufsIntegrated.toFixed(1)} sub="LUFS" />
            <Stat label="Crest" value={analysis.crestFactorDb.toFixed(1)} sub="dB" />
            <Stat label="PLR" value={analysis.dynamicRangeDb.toFixed(1)} sub="dB (peak-to-loudness)" />
            <Stat label="LRA" value={analysis.loudnessRange !== null ? analysis.loudnessRange.toFixed(1) : '--'} sub="LU" />
            <Stat label="Correlation" value={analysis.correlation.toFixed(2)} />
            <Stat label="Spectral edge" value={fmtHz(analysis.spectralEdgeHz)} sub="content limit" />
            <Stat label="Effective bits" value={analysis.effectiveBits ?? '--'} sub={metadata.bitDepth ? `declared ${metadata.bitDepth}` : ''} />
            <Stat label="Clip events" value={analysis.clipEvents} tone={analysis.clipEvents > 0 ? 'warn' : undefined} />
          </div>
          {metadata.bitDepth && analysis.effectiveBits && analysis.effectiveBits < metadata.bitDepth - 1 && (
            <div style={{ marginTop: 8 }}>
              <Note tone="warn">
                The file declares {metadata.bitDepth} bits but the samples only use about {analysis.effectiveBits}. This
                is what an upconverted file looks like.
              </Note>
            </div>
          )}
          {analysis.spectralEdgeHz > 0 && analysis.spectralEdgeHz < metadata.sampleRate * 0.35 && metadata.lossless && (
            <div style={{ marginTop: 8 }}>
              <Note tone="warn">
                Content stops at {fmtHz(analysis.spectralEdgeHz)} in a lossless container. That is the signature of
                material that passed through a lossy codec before being re-encoded.
              </Note>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function DacInspector() {
  const { chain, compiled, activeSystem } = useActive();
  const setChain = useApp((s) => s.setChain);
  const dac = registry.get(chain.dacId) as DacProfile | null;
  const fs = engine.sampleRate || 48000;
  const os = compiled?.dac.oversample ?? 1;

  const firCurve = useMemo(() => firToCurve(compiled?.dac.firTaps ?? null, fs * os), [compiled?.dac.firTaps, fs, os]);
  const analogCurve = useMemo(() => cascadeToCurve(unflatten(compiled?.dac.analogEq ?? new Float32Array(0)), fs), [compiled?.dac.analogEq, fs]);
  const harmonics = useMemo(
    () =>
      Array.from(compiled?.dac.harmonics.amps ?? []).map((a, i) => ({
        order: i + 2,
        db: a > 0 ? 20 * Math.log10(a) : -200,
      })),
    [compiled?.dac.harmonics],
  );

  if (!dac || !compiled) return <div className="panel-section dim">No DAC selected.</div>;

  return (
    <>
      <ProfileHeader profile={dac} />

      <div className="panel-section">
        <SectionTitle>Reconstruction filter</SectionTitle>
        <select
          value={chain.dac.filterId ?? dac.defaultFilterId}
          onChange={(e) => setChain(activeSystem, { dac: { ...chain.dac, filterId: e.target.value } })}
          aria-label="Digital filter"
        >
          {dac.filters.map((f) => (
            <option key={f.id} value={f.id}>
              {f.label}
            </option>
          ))}
        </select>
        <div style={{ marginTop: 8 }}>
          <FrequencyPlot
            series={[{ curve: firCurve, color: '#45c8b8', label: 'Reconstruction' }]}
            dbMin={-140}
            dbMax={12}
            fMax={Math.min(fs * os, 200000) / 2}
            height={140}
            yLabel="dB"
          />
        </div>
        <div className="stat-grid" style={{ marginTop: 6 }}>
          <Stat label="Mode" value={compiled.dac.nosHold ? 'NOS (hold)' : dac.filters.find((f) => f.id === (chain.dac.filterId ?? dac.defaultFilterId))?.mode ?? '--'} />
          <Stat label="Taps" value={compiled.dac.firTaps?.length ?? (compiled.dac.nosHold ? 'n/a' : 0)} />
          <Stat label="Oversampling" value={`${os}x`} sub={`${((fs * os) / 1000).toFixed(1)} kHz internal`} />
          <Stat label="Droop comp." value={compiled.dac.droopTaps ? `${compiled.dac.droopTaps.length} taps` : 'none'} />
        </div>
        <div style={{ marginTop: 8 }}>
          <div className="tiny faint" style={{ marginBottom: 4 }}>Impulse response (at the oversampled rate)</div>
          <ImpulsePlot taps={compiled.dac.firTaps} height={110} sampleRate={fs * os} showStep />
        </div>
      </div>

      <div className="panel-section">
        <SectionTitle>Conversion</SectionTitle>
        <div className="stat-grid">
          <Stat label="Architecture" value={dac.conversion.architecture} />
          <Stat label="Core" value={dac.conversion.core} />
          <Stat label="Effective bits" value={compiled.dac.effectiveBits || 'float'} />
          <Stat label="Ladder INL" value={compiled.dac.ladderMismatchLsb ? `${compiled.dac.ladderMismatchLsb} LSB` : 'n/a'} />
          <Stat label="Jitter" value={`${compiled.dac.jitterPsRms} ps`} sub={['random', 'periodic', 'mixed'][compiled.dac.jitterKind]} />
          <Stat label="Noise shaper" value={dac.conversion.noiseShaperOrder ?? '--'} sub="order" />
        </div>
      </div>

      <div className="panel-section">
        <SectionTitle>Analog output</SectionTitle>
        <FrequencyPlot series={[{ curve: analogCurve, color: '#6ea8ff' }]} dbMin={-6} dbMax={3} height={120} />
        <div className="stat-grid" style={{ marginTop: 6 }}>
          <Stat label="Stage" value={dac.analog.stage} />
          <Stat label="Output" value={`${chain.dac.balanced && dac.analog.outputVrmsBal ? dac.analog.outputVrmsBal : dac.analog.outputVrmsSE} Vrms`} sub={chain.dac.balanced ? 'balanced' : 'single-ended'} />
          <Stat label="Z out" value={`${dac.analog.outputImpedanceOhms} Ω`} />
          <Stat label="Slew limit" value={dac.analog.slewRateVus ? `${dac.analog.slewRateVus} V/µs` : 'none'} />
        </div>
        <Toggle
          label="Balanced output"
          checked={chain.dac.balanced}
          onChange={(v) => setChain(activeSystem, { dac: { ...chain.dac, balanced: v } })}
        />
      </div>

      <div className="panel-section">
        <SectionTitle>Nonlinearity and noise</SectionTitle>
        <div className="grid2">
          <div>
            <div className="tiny faint" style={{ marginBottom: 4 }}>Transfer function</div>
            <TransferPlot fn={makeShaperFn(compiled.dac.nonlinear)} height={130} />
          </div>
          <div>
            <div className="tiny faint" style={{ marginBottom: 4 }}>Harmonic structure at -6 dBFS</div>
            <HarmonicBars harmonics={harmonics} height={130} />
          </div>
        </div>
        <div className="stat-grid" style={{ marginTop: 6 }}>
          <Stat label="Model" value={NL_NAMES[compiled.dac.nonlinear.kind]} />
          <Stat label="THD+N" value={dac.thdPlusNDb !== undefined ? `${dac.thdPlusNDb} dB` : '--'} />
          <Stat label="Dynamic range" value={`${dac.dynamicRangeDb} dB`} />
          <Stat label="Noise floor" value={`${(20 * Math.log10(Math.max(1e-12, compiled.dac.noise.levelLinear))).toFixed(0)} dBFS`} sub={['white', 'pink', 'thermal', 'shaped'][compiled.dac.noise.shape]} />
          <Stat label="Crosstalk" value={`${dac.channels.crosstalkDb} dB`} />
          <Stat label="Imbalance" value={`${dac.channels.imbalanceDb ?? 0} dB`} />
        </div>
      </div>

      <ProvenanceSection profile={dac} />
    </>
  );
}

function AmpInspector() {
  const { chain, compiled, headroom, activeSystem } = useActive();
  const setVolume = useApp((s) => s.setVolume);
  const amp = registry.get(chain.ampId) as AmpProfile | null;
  const out = registry.get(chain.outputId);
  const fs = engine.sampleRate || 48000;

  const respCurve = useMemo(() => cascadeToCurve(unflatten(compiled?.amp.eq ?? new Float32Array(0)), fs), [compiled?.amp.eq, fs]);
  const loadCurve = useMemo(
    () => (amp && out ? loadInteractionCurve(amp, out as SpeakerProfile | HeadphoneProfile) : flat()),
    [amp, out],
  );
  const harmonics = useMemo(
    () =>
      Array.from(compiled?.amp.harmonics.amps ?? []).map((a, i) => ({
        order: i + 2,
        db: a > 0 ? 20 * Math.log10(a) : -200,
      })),
    [compiled?.amp.harmonics],
  );

  if (!amp || !compiled) return <div className="panel-section dim">No amplifier selected.</div>;

  let loadSpread = 0;
  for (const [, db] of loadCurve.points) loadSpread = Math.max(loadSpread, Math.abs(db));

  return (
    <>
      <ProfileHeader profile={amp} />

      <div className="panel-section">
        <SectionTitle>Drive</SectionTitle>
        <Slider
          label="Volume"
          min={0}
          max={1}
          step={0.005}
          value={chain.amp.volume}
          onChange={(v) => setVolume(activeSystem, v)}
          format={(v) => `${(20 * Math.log10(Math.max(1e-4, v))).toFixed(1)} dB`}
        />
        <div className="stat-grid">
          <Stat label="Voltage gain" value={`${amp.gainDb} dB`} />
          <Stat
            label="Headroom"
            value={headroom?.valid ? `${headroom.headroomDb.toFixed(1)} dB` : 'invalid'}
            tone={!headroom?.valid ? 'danger' : headroom.headroomDb < 3 ? 'warn' : 'ok'}
          />
          <Stat label="Clip ceiling" value={(20 * Math.log10(Math.max(1e-6, compiled.amp.clipCeiling))).toFixed(1)} sub="dB internal" />
          <Stat label="Clip type" value={['soft', 'hard', 'asymmetric'][compiled.amp.clipKind]} />
        </div>
        {headroom && <Note tone={!headroom.valid ? 'danger' : headroom.headroomDb < 3 ? 'warn' : undefined}>{headroom.note}</Note>}
      </div>

      <div className="panel-section">
        <SectionTitle>Response and load interaction</SectionTitle>
        <FrequencyPlot
          series={[
            { curve: respCurve, color: '#6ea8ff', label: 'Amplifier' },
            { curve: loadCurve, color: '#ffc857', label: 'Load interaction', dashed: true },
          ]}
          dbMin={-6}
          dbMax={3}
          height={150}
        />
        <div className="row tiny" style={{ gap: 12, marginTop: 4 }}>
          <span style={{ color: '#6ea8ff' }}>— amplifier bandwidth</span>
          <span style={{ color: '#ffc857' }}>-- into this transducer</span>
        </div>
        <div className="stat-grid" style={{ marginTop: 6 }}>
          <Stat label="Z out" value={`${amp.output.outputImpedanceOhms} Ω`} />
          <Stat label="Damping" value={amp.output.dampingFactor?.toFixed(0) ?? '--'} sub="into 8 Ω" />
          <Stat label="Feedback" value={`${amp.output.feedbackDb ?? 0} dB`} />
          <Stat
            label="Load deviation"
            value={`${loadSpread.toFixed(2)} dB`}
            tone={loadSpread > 1 ? 'warn' : undefined}
            title="How much this amplifier's output impedance lets the transducer's impedance curve modify the response"
          />
        </div>
        {loadSpread > 0.3 && (
          <Note>
            This amplifier's {amp.output.outputImpedanceOhms} Ω source impedance lets the transducer's impedance curve
            print itself onto the frequency response, up to {loadSpread.toFixed(2)} dB. Swap to a low-impedance
            amplifier and this curve flattens.
          </Note>
        )}
      </div>

      <div className="panel-section">
        <SectionTitle>Nonlinearity</SectionTitle>
        <div className="grid2">
          <div>
            <div className="tiny faint" style={{ marginBottom: 4 }}>Transfer function</div>
            <TransferPlot fn={makeShaperFn(compiled.amp.nonlinear)} height={130} color="#ff9f6e" />
          </div>
          <div>
            <div className="tiny faint" style={{ marginBottom: 4 }}>Harmonic structure</div>
            <HarmonicBars harmonics={harmonics} height={130} />
          </div>
        </div>
        <div className="stat-grid" style={{ marginTop: 6 }}>
          <Stat label="Topology" value={amp.topology} />
          <Stat label="Model" value={NL_NAMES[compiled.amp.nonlinear.kind]} />
          <Stat label="THD" value={`${amp.thdPercent}%`} />
          <Stat label="SNR" value={`${amp.snrDb} dB`} />
          <Stat label="Sag" value={`${compiled.amp.sagDb} dB`} />
          <Stat label="Slew" value={amp.dynamics.slewRateVus ? `${amp.dynamics.slewRateVus} V/µs` : '--'} />
          <Stat label="Transformer" value={compiled.amp.transformerSat > 0 ? 'yes' : 'no'} sub={compiled.amp.transformerSat > 0 ? `LF ${compiled.amp.transformerLfHz} Hz` : ''} />
          <Stat label="Crosstalk" value={`${amp.channels.crosstalkDb} dB`} />
        </div>
      </div>

      <ProvenanceSection profile={amp} />
    </>
  );
}

function OutputInspector() {
  const { chain, compiled } = useActive();
  const out = registry.get(chain.outputId);
  const fs = engine.sampleRate || 48000;
  const firCurve = useMemo(() => firToCurve(compiled?.output.firTaps ?? null, fs), [compiled?.output.firTaps, fs]);
  const eqCurve = useMemo(() => cascadeToCurve(unflatten(compiled?.output.eq ?? new Float32Array(0)), fs), [compiled?.output.eq, fs]);
  const total = useMemo<FRCurve>(() => {
    const points: Array<readonly [number, number]> = [];
    for (let i = 0; i < 90; i++) {
      const f = 10 * Math.pow(3000, i / 89);
      if (f > fs / 2) break;
      points.push([f, evalFR(firCurve, f) + evalFR(eqCurve, f)]);
    }
    return { kind: 'fr', points: points as FRCurve['points'], interp: 'log-pchip' };
  }, [firCurve, eqCurve, fs]);

  if (!out || !compiled) return <div className="panel-section dim">No output transducer selected.</div>;
  const speaker = isSpeaker(out) ? out : null;
  const hp = isHeadphone(out) ? out : null;
  // Both transducer categories carry `response`; the union does not.
  const transducer = (speaker ?? hp) as SpeakerProfile | HeadphoneProfile | null;
  if (!transducer) return <div className="panel-section dim">Selected profile is not an output transducer.</div>;

  return (
    <>
      <ProfileHeader profile={out} />

      <div className="panel-section">
        <SectionTitle>{hp ? 'Coupler response' : 'Anechoic response'}</SectionTitle>
        <FrequencyPlot
          series={[
            { curve: transducer.response, color: '#3a4653', dashed: true, label: 'Profile' },
            { curve: total, color: '#45c8b8', label: 'Compiled' },
          ]}
          dbMin={-30}
          dbMax={12}
          height={160}
        />
        <div className="row tiny" style={{ gap: 12, marginTop: 4 }}>
          <span className="faint">-- profile data</span>
          <span style={{ color: '#45c8b8' }}>— what is running (FIR + correction + resonances)</span>
        </div>
        <div className="stat-grid" style={{ marginTop: 6 }}>
          <Stat label="FIR taps" value={compiled.output.firTaps?.length ?? 0} />
          <Stat label="Correction" value={`${compiled.output.eq.length / 5} biquads`} />
          <Stat label="Mode" value={compiled.output.mode === 1 ? 'headphone' : 'loudspeaker'} />
          <Stat label="Oversampling" value={`${compiled.output.oversample}x`} />
        </div>
        <div style={{ marginTop: 8 }}>
          <div className="tiny faint" style={{ marginBottom: 4 }}>Impulse and step response</div>
          <ImpulsePlot taps={compiled.output.firTaps} height={120} sampleRate={fs} showStep color="#45c8b8" />
        </div>
      </div>

      {speaker && (
        <>
          <div className="panel-section">
            <SectionTitle>Impedance</SectionTitle>
            <ImpedancePlot curve={speaker.impedance} height={150} />
            <div className="stat-grid" style={{ marginTop: 6 }}>
              <Stat label="Nominal" value={`${speaker.impedance.nominalOhms ?? '--'} Ω`} />
              <Stat label="Minimum" value={`${speaker.impedance.minOhms?.toFixed(1) ?? '--'} Ω`} tone={(speaker.impedance.minOhms ?? 8) < 3 ? 'warn' : undefined} />
              <Stat label="Sensitivity" value={`${speaker.sensitivityDb} dB`} sub="2.83 V / 1 m" />
              <Stat label="Enclosure" value={speaker.cabinet.enclosure} />
            </div>
          </div>
          <div className="panel-section">
            <SectionTitle>Alignment and directivity</SectionTitle>
            <div className="stat-grid">
              <Stat label="-3 dB" value={`${speaker.cabinet.f3Hz ?? '--'} Hz`} />
              <Stat label="-6 dB" value={`${speaker.cabinet.f6Hz ?? '--'} Hz`} />
              <Stat label="Port tuning" value={speaker.cabinet.portTuningHz ? `${speaker.cabinet.portTuningHz.toFixed(0)} Hz` : 'n/a'} />
              <Stat label="Coverage" value={`${speaker.directivity.coverageAngleDeg ?? '--'}°`} />
              <Stat label="Baffle step" value={`${speaker.directivity.baffleStepHz ?? '--'} Hz`} sub={`${speaker.directivity.baffleStepDb ?? 0} dB`} />
              <Stat label="Crossovers" value={speaker.crossover.map((c) => `${c.freqHz}`).join(' / ') || 'none'} sub="Hz" />
            </div>
            <Note>
              Directivity here is a parameterised model, not measured data. Import a Klippel NFS or spinorama data set in
              the Measurement Lab to replace it.
            </Note>
          </div>
        </>
      )}

      {hp && <HeadphoneControls profile={hp} />}

      <div className="panel-section">
        <SectionTitle>Distortion</SectionTitle>
        <HarmonicBars
          harmonics={Array.from(compiled.output.harmonics.amps).map((a, i) => ({
            order: i + 2,
            db: a > 0 ? 20 * Math.log10(a) : -200,
          }))}
          height={120}
        />
        <div className="stat-grid" style={{ marginTop: 6 }}>
          <Stat label="Model" value={NL_NAMES[compiled.output.nonlinear.kind]} />
          <Stat label="Excursion knee" value={`${compiled.output.excursionKneeHz.toFixed(0)} Hz`} />
          <Stat label="Rise" value={`${compiled.output.excursionRiseDbOct} dB/oct`} />
          <Stat label="Compression" value={`${compiled.output.compressionDb} dB`} />
        </div>
      </div>

      <ProvenanceSection profile={out} />
    </>
  );
}

function HeadphoneControls({ profile }: { profile: HeadphoneProfile }) {
  const { chain, activeSystem } = useActive();
  const setChain = useApp((s) => s.setChain);
  const hp = chain.headphone;
  const patch = (p: Partial<typeof hp>) => setChain(activeSystem, { headphone: { ...hp, ...p } });

  return (
    <div className="panel-section">
      <SectionTitle>Headphone path</SectionTitle>
      <div className="stat-grid" style={{ marginBottom: 8 }}>
        <Stat label="Type" value={profile.headphoneType} />
        <Stat label="Fit" value={profile.fit} />
        <Stat label="Bias" value={profile.biasVolts ? `${profile.biasVolts} V` : 'n/a'} />
        <Stat label="Capacitance" value={profile.capacitancePf ? `${profile.capacitancePf} pF` : 'n/a'} />
        <Stat label="Sensitivity" value={profile.sensitivityDbVrms ? `${profile.sensitivityDbVrms} dB` : '--'} sub={profile.requiresEnergiser ? '/100 Vrms' : '/Vrms'} />
        <Stat label="Energiser" value={profile.requiresEnergiser ? 'required' : 'no'} />
      </div>

      <Slider
        label="Seal / fit"
        min={-1}
        max={1}
        step={0.05}
        value={hp.seal}
        onChange={(v) => patch({ seal: v })}
        format={(v) => `${v > 0 ? '+' : ''}${(v * (profile.sealSensitivityDb ?? 3)).toFixed(1)} dB LF`}
      />
      <Slider
        label="Channel balance"
        min={-6}
        max={6}
        step={0.1}
        value={hp.balanceDb}
        onChange={(v) => patch({ balanceDb: v })}
        format={(v) => `${v > 0 ? 'R+' : v < 0 ? 'L+' : ''}${Math.abs(v).toFixed(1)} dB`}
      />
      <div className="field">
        <label>Crossfeed</label>
        <Segmented
          small
          value={hp.crossfeed}
          onChange={(v) => patch({ crossfeed: v })}
          options={[
            { value: 'off', label: 'Off' },
            { value: 'low', label: 'Low' },
            { value: 'medium', label: 'Med' },
            { value: 'high', label: 'High' },
            { value: 'custom', label: 'Custom' },
          ]}
          ariaLabel="Crossfeed"
        />
      </div>
      {hp.crossfeed === 'custom' && (
        <>
          <Slider label="Amount" min={0} max={0.8} step={0.01} value={hp.crossfeedAmount} onChange={(v) => patch({ crossfeedAmount: v })} format={(v) => v.toFixed(2)} />
          <Slider label="Interaural delay" min={100} max={500} step={5} value={hp.crossfeedDelayUs} onChange={(v) => patch({ crossfeedDelayUs: v })} format={(v) => `${v} µs`} />
          <Slider label="Head shadow" min={200} max={2000} step={10} value={hp.crossfeedLpHz} onChange={(v) => patch({ crossfeedLpHz: v })} format={(v) => `${v} Hz`} />
        </>
      )}
      {profile.target && (
        <Toggle
          label={`Apply target compensation (${profile.target.name})`}
          checked={hp.targetCompensation}
          onChange={(v) => patch({ targetCompensation: v })}
        />
      )}
      {profile.requiresEnergiser && (
        <Note>
          This is an electrostatic transducer. It cannot be driven by a conventional headphone amplifier; the engine
          rejects that pairing rather than pretending to simulate it.
        </Note>
      )}
    </div>
  );
}

function SpatialInspector() {
  const { chain, compiled, activeSystem } = useActive();
  const setRoom = useApp((s) => s.setRoom);
  const setChain = useApp((s) => s.setChain);
  const out = registry.get(chain.outputId);
  const headphoneMode = !!out && isHeadphone(out);
  const room = chain.room;

  if (headphoneMode) {
    return (
      <div className="panel-section">
        <SectionTitle>Interaural path</SectionTitle>
        <Note>
          A headphone is selected, so the loudspeaker room model is disabled. Crossfeed and interaural processing are
          configured on the output node.
        </Note>
        {compiled && (
          <div className="stat-grid" style={{ marginTop: 8 }}>
            <Stat label="Crossfeed" value={compiled.spatial.crossfeedAmount.toFixed(2)} />
            <Stat label="Delay" value={`${((compiled.spatial.crossfeedDelaySamples / (engine.sampleRate || 48000)) * 1e6).toFixed(0)} µs`} />
            <Stat label="Width" value={compiled.spatial.width.toFixed(2)} />
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="panel-section">
        <SectionTitle>Listening room</SectionTitle>
        <Toggle label="Enable room model" checked={room.enabled} onChange={(v) => setRoom(activeSystem, { enabled: v })} />
        <div className="field">
          <label>Preset</label>
          <Segmented
            small
            value={room.preset}
            onChange={(v) => {
              if (v === 'custom') setRoom(activeSystem, { preset: 'custom' });
              else {
                void import('../../stores/defaults').then(({ applyRoomPreset }) => {
                  setRoom(activeSystem, applyRoomPreset(room, v as 'small' | 'medium' | 'large'));
                });
              }
            }}
            options={[
              { value: 'small', label: 'Small' },
              { value: 'medium', label: 'Medium' },
              { value: 'large', label: 'Large' },
              { value: 'custom', label: 'Custom' },
            ]}
            ariaLabel="Room preset"
          />
        </div>
        <Slider label="Width" min={2} max={12} step={0.1} value={room.widthM} onChange={(v) => setRoom(activeSystem, { widthM: v, preset: 'custom' })} format={(v) => `${v.toFixed(1)} m`} />
        <Slider label="Length" min={2} max={16} step={0.1} value={room.lengthM} onChange={(v) => setRoom(activeSystem, { lengthM: v, preset: 'custom' })} format={(v) => `${v.toFixed(1)} m`} />
        <Slider label="Height" min={2} max={6} step={0.05} value={room.heightM} onChange={(v) => setRoom(activeSystem, { heightM: v, preset: 'custom' })} format={(v) => `${v.toFixed(2)} m`} />
        <Slider label="Speaker spacing" min={0.8} max={6} step={0.05} value={room.speakerSpacingM} onChange={(v) => setRoom(activeSystem, { speakerSpacingM: v, preset: 'custom' })} format={(v) => `${v.toFixed(2)} m`} />
        <Slider label="Listener distance" min={0.8} max={8} step={0.05} value={room.listenerDistanceM} onChange={(v) => setRoom(activeSystem, { listenerDistanceM: v, preset: 'custom' })} format={(v) => `${v.toFixed(2)} m`} />
        <Slider label="Toe-in" min={0} max={45} step={1} value={room.toeInDeg} onChange={(v) => setRoom(activeSystem, { toeInDeg: v })} format={(v) => `${v}°`} />
      </div>

      <div className="panel-section">
        <SectionTitle>Boundaries</SectionTitle>
        <Slider label="Wall absorption" min={0.02} max={0.95} step={0.01} value={room.absorptionWalls} onChange={(v) => setRoom(activeSystem, { absorptionWalls: v, preset: 'custom' })} format={(v) => v.toFixed(2)} />
        <Slider label="Floor absorption" min={0.02} max={0.95} step={0.01} value={room.absorptionFloor} onChange={(v) => setRoom(activeSystem, { absorptionFloor: v, preset: 'custom' })} format={(v) => v.toFixed(2)} />
        <Slider label="Ceiling absorption" min={0.02} max={0.95} step={0.01} value={room.absorptionCeiling} onChange={(v) => setRoom(activeSystem, { absorptionCeiling: v, preset: 'custom' })} format={(v) => v.toFixed(2)} />
        <Slider label="Room mix" min={0} max={100} step={1} value={room.mixPercent} onChange={(v) => setRoom(activeSystem, { mixPercent: v })} format={(v) => `${v}%`} />
        <div className="grid2" style={{ marginTop: 6 }}>
          <Toggle label="Floor" checked={room.floorReflection} onChange={(v) => setRoom(activeSystem, { floorReflection: v })} />
          <Toggle label="Ceiling" checked={room.ceilingReflection} onChange={(v) => setRoom(activeSystem, { ceilingReflection: v })} />
          <Toggle label="Side walls" checked={room.sideReflection} onChange={(v) => setRoom(activeSystem, { sideReflection: v })} />
          <Toggle label="Front/rear" checked={room.rearReflection} onChange={(v) => setRoom(activeSystem, { rearReflection: v })} />
        </div>
        {compiled && (
          <div className="stat-grid" style={{ marginTop: 8 }}>
            <Stat label="Reflections" value={compiled.spatial.reflections.length / 4} sub="image sources" />
            <Stat label="RT60" value={`${compiled.spatial.lateDecaySeconds.toFixed(2)} s`} sub="Sabine estimate" />
            <Stat label="Late level" value={`${(20 * Math.log10(Math.max(1e-6, compiled.spatial.lateGain))).toFixed(1)} dB`} />
          </div>
        )}
      </div>

      <div className="panel-section">
        <SectionTitle>Stereo</SectionTitle>
        <Toggle label="Enable spatial processing" checked={chain.spatial.enabled} onChange={(v) => setChain(activeSystem, { spatial: { ...chain.spatial, enabled: v } })} />
        <Slider label="Width" min={0} max={2} step={0.01} value={chain.spatial.width} onChange={(v) => setChain(activeSystem, { spatial: { ...chain.spatial, width: v } })} format={(v) => (v === 1 ? 'normal' : v === 0 ? 'mono' : v.toFixed(2))} />
      </div>
    </>
  );
}

function MasterInspector() {
  const engineSettings = useApp((s) => s.project.engine);
  const setEngine = useApp((s) => s.setEngine);
  const limiterEngaged = useApp((s) => s.limiterEngaged);
  const limiterMax = useApp((s) => s.limiterMaxReduction);

  return (
    <div className="panel-section">
      <SectionTitle>Master and safety</SectionTitle>
      <Slider label="Master gain" min={-60} max={0} step={0.5} value={engineSettings.masterGainDb} onChange={(v) => setEngine({ masterGainDb: v })} format={(v) => `${v.toFixed(1)} dB`} />
      <Toggle label="Safety limiter" checked={engineSettings.safetyLimiter} onChange={(v) => setEngine({ safetyLimiter: v })} />
      <div className="stat-grid" style={{ marginTop: 8 }}>
        <Stat label="Limiter" value={limiterEngaged ? 'ENGAGED' : 'idle'} tone={limiterEngaged ? 'warn' : 'ok'} />
        <Stat label="Max reduction" value={`${limiterMax.toFixed(2)} dB`} tone={limiterMax > 1 ? 'warn' : undefined} />
      </div>
      <Note>
        The safety limiter exists to protect your hearing and your equipment, not to shape the sound. When it engages it
        says so, so limiter behaviour is never mistaken for amplifier behaviour.
      </Note>
    </div>
  );
}

// ---------------------------------------------------------------------------

function ProfileHeader({ profile }: { profile: AnyProfile }) {
  return (
    <div className="panel-section">
      <div className="row" style={{ alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="dim tiny" style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>{profile.manufacturer}</div>
          <div className="bright" style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.25 }}>{profile.model}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="mono" style={{ color: 'var(--accent)' }}>{fmtPrice(profile.price)}</div>
          <div className="faint tiny">{PRICE_STATUS_LABEL[profile.price.status]}</div>
        </div>
      </div>
      <div style={{ marginTop: 6 }}>
        <ConfidenceBar profile={profile} />
      </div>
      <div className="row" style={{ marginTop: 5 }}>
        <ConfidenceBadge value={profile.confidence} />
        {profile.year && <span className="faint tiny">{profile.year}</span>}
        {profile.countryOfOrigin && <span className="faint tiny">· {profile.countryOfOrigin}</span>}
        <span className="faint tiny">· profile v{profile.version}</span>
      </div>
      {profile.notes && <p className="dim small" style={{ marginBottom: 0 }}>{profile.notes}</p>}
    </div>
  );
}

function ProvenanceSection({ profile }: { profile: AnyProfile }) {
  return (
    <div className="panel-section">
      <SectionTitle>Provenance</SectionTitle>
      <Note tone={profile.confidence === 'UNKNOWN' ? 'warn' : undefined}>
        {profile.provenanceNote ?? 'No provenance note recorded for this profile.'}
      </Note>
      {profile.measurement && (
        <div className="stat-grid" style={{ marginTop: 8 }}>
          <Stat label="Rig" value={profile.measurement.rig ?? '--'} />
          <Stat label="Date" value={profile.measurement.date ?? '--'} />
          <Stat label="Smoothing" value={profile.measurement.smoothingOct ? `1/${profile.measurement.smoothingOct} oct` : 'none'} />
        </div>
      )}
      <p className="faint tiny" style={{ marginBottom: 0, marginTop: 8 }}>
        Profile fidelity is stated, never claimed. This is a computational model whose parameters come from the sources
        described above -- it is not an assertion that the simulation is indistinguishable from the physical device.
      </p>
    </div>
  );
}

function ProfileDetail({ profile }: { profile: AnyProfile }) {
  return (
    <>
      <ProfileHeader profile={profile} />
      <div className="panel-section">
        <SectionTitle>Tags</SectionTitle>
        <div className="row wrap">
          {(profile.tags ?? []).map((t) => (
            <span className="tag" key={t}>
              {t}
            </span>
          ))}
        </div>
      </div>
      <ProvenanceSection profile={profile} />
    </>
  );
}

/** Reproduce the worklet's shaper on the main thread, for plotting only. */
function makeShaperFn(nl: { kind: number; amount: number; headroom: number; bias: number; asymmetry: number }) {
  return (x: number) => {
    if (!nl || nl.kind === 0 || nl.amount <= 0) return x;
    const u = x / nl.headroom;
    let y: number;
    const asym = nl.asymmetry;
    switch (nl.kind) {
      case 4: {
        const up = 1 + asym * 0.85;
        const dn = 1 - asym * 0.45;
        const k = u >= 0 ? up : dn;
        const t = Math.tanh(u * k);
        y = t + nl.bias * 0.12 * (t * t - 0.33) * (u >= 0 ? 1 : -0.35);
        break;
      }
      case 5: {
        const a = Math.abs(u);
        const s = Math.sign(u);
        const v = s * (a - (a * a * a) / 3);
        y = (a > 1 ? s * 0.6667 : v) * (1 + asym * 0.1 * s) * 1.5;
        break;
      }
      case 6: {
        const a = Math.abs(u);
        const s = Math.sign(u);
        const g = a / (a + 0.004);
        y = Math.tanh(s * a * (g * 0.55 + 0.45) * (1 + asym * 0.1 * s));
        break;
      }
      case 7: {
        const a = Math.abs(u);
        if (a < 0.85) y = u + 0.02 * u * u * u;
        else {
          const over = a - 0.85;
          y = Math.sign(u) * (0.85 + over / (1 + over * 9));
        }
        break;
      }
      case 11:
        y = (Math.tanh((u - asym * 0.18 * u * u) * 0.92) / 0.92) * 0.96;
        break;
      case 12: {
        const a = u * (1 + nl.bias * 0.08);
        y = (a * (1 - a * a * 0.055)) / (1 + a * a * 0.02);
        break;
      }
      case 13:
        y = Math.tanh((u - asym * 0.09 * u * u + 0.03 * u * u * u) * 0.9) / 0.9;
        break;
      case 14: {
        const a = u * (1 + nl.bias * 0.05);
        y = Math.tanh((a - asym * 0.14 * a * a - 0.06 * a * a * a) * 0.88) / 0.88;
        break;
      }
      case 10:
        y = u - 0.004 * u * u * u;
        break;
      default:
        y = Math.tanh(u);
    }
    const wet = y * nl.headroom;
    return x + nl.amount * (wet - x);
  };
}
